/**
 * API service for filter data (countries, brands, categories, retailers, stores)
 *
 * Provides both:
 *   - A combined fetchFilterOptions() that hits GET /filters/options (preferred)
 *   - Individual fetch helpers for backward compatibility
 */
import api from './api';

// ---- Types ----

export interface StoreOption {
  store_id: string;
  store_name: string;
  store_code: string;
  country?: string | null;
  city?: string | null;
  retailer_name?: string | null;
}

export interface FilterOptionsData {
  countries: string[];
  brands: string[];
  categories: string[];
  subcategories: string[];
  retailers: string[];
  cities: string[];
  stores: StoreOption[];
}

/** Legacy type alias */
export interface FiltersData {
  countries: string[];
  brands: string[];
  categories: string[];
  subcategories: string[];
  retailers: string[];
  cities: string[];
}

// ---- Combined fetch (preferred) ----

/**
 * Fetch all filter options in a single API call.
 * Supports optional cascading filters.
 */
export const fetchFilterOptions = async (params?: {
  country?: string;
  category?: string;
  retailer?: string;
}): Promise<FilterOptionsData> => {
  try {
    const response = await api.getFilterOptions(params);
    return response.data;
  } catch (error) {
    // Fallback: fetch individually
    return fetchFilterOptionsFallback();
  }
};

/**
 * Fallback that fetches each filter list individually.
 * Used when /filters/options endpoint is unavailable.
 */
async function fetchFilterOptionsFallback(): Promise<FilterOptionsData> {
  const [countriesRes, brandsRes, categoriesRes, retailersRes] = await Promise.all([
    api.getCountries().catch(() => ({ data: [] })),
    api.getBrands().catch(() => ({ data: [] })),
    api.getCategories().catch(() => ({ data: [] })),
    api.getRetailers().catch(() => ({ data: [] })),
  ]);

  return {
    countries: countriesRes.data,
    brands: brandsRes.data,
    categories: categoriesRes.data,
    retailers: retailersRes.data,
    subcategories: [],
    cities: [],
    stores: [],
  };
}

// ---- Legacy combined fetch ----

/**
 * Fetch all filter data for dashboard (legacy - uses individual endpoints).
 * @deprecated Use fetchFilterOptions() instead.
 */
export const fetchAllFilters = async (): Promise<FiltersData> => {
  try {
    const [countriesRes, brandsRes, categoriesRes, retailersRes] = await Promise.all([
      api.getCountries(),
      api.getBrands(),
      api.getCategories(),
      api.getRetailers(),
    ]);

    return {
      countries: countriesRes.data,
      brands: brandsRes.data,
      categories: categoriesRes.data,
      retailers: retailersRes.data,
      subcategories: [],
      cities: [],
    };
  } catch (error) {
    throw error;
  }
};

// ---- Individual fetchers (backward compatibility) ----

export const fetchCountries = async (): Promise<string[]> => {
  const response = await api.getCountries();
  return response.data;
};

export const fetchBrands = async (): Promise<string[]> => {
  const response = await api.getBrands();
  return response.data;
};

export const fetchCategories = async (): Promise<string[]> => {
  const response = await api.getCategories();
  return response.data;
};

export const fetchSubcategories = async (category?: string): Promise<string[]> => {
  const response = await api.getSubcategories(category);
  return response.data;
};

export const fetchRetailers = async (): Promise<string[]> => {
  const response = await api.getRetailers();
  return response.data;
};

export const fetchCities = async (country?: string): Promise<string[]> => {
  const response = await api.getCities(country);
  return response.data;
};

export const fetchStores = async (params?: {
  country?: string;
  retailer?: string;
}): Promise<StoreOption[]> => {
  const response = await api.getFilterStores(params);
  return response.data;
};
